/**
 * Year6NewContent — return-loop UI surfaces for the new-content notifications system.
 *
 * Three exports:
 *   1. NewContentBanner       — top of Parent View, localStorage-driven re-entry banner
 *   2. ParentSubscribeCard    — above paywall, captures parent emails for notifications
 *   3. UploadsTodayPill       — Control Panel feedback line ("📈 12 new uploads today")
 *
 * Storage key: `year6_seen_content_{slug}` = `{photos, messages, ts}`
 * Banner shows once per session per slug — sessionStorage `year6_banner_shown_{slug}`.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, EnvelopeSimple, X, Check, TrendUp } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEEN_KEY = (slug) => `year6_seen_content_${slug}`;
const SESSION_KEY = (slug) => `year6_banner_shown_${slug}`;

/* Read last-seen snapshot from localStorage. Returns null if never seen. */
function readSeen(slug) {
  try {
    const raw = localStorage.getItem(SEEN_KEY(slug));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/* Persist current snapshot. Used after dismiss / view-what's-new click. */
function writeSeen(slug, photos, messages) {
  try {
    localStorage.setItem(SEEN_KEY(slug), JSON.stringify({
      photos: photos || 0,
      messages: messages || 0,
      ts: Date.now(),
    }));
  } catch { /* ignore */ }
}


// ─────────────────────────────────────────────────────────────────
// 1. RE-ENTRY BANNER — top of Parent View
// ─────────────────────────────────────────────────────────────────
export function NewContentBanner({ slug, stats, purchased }) {
  const [visible, setVisible] = useState(false);
  const [delta, setDelta] = useState({ photos: 0, messages: 0 });
  const [fading, setFading] = useState(false);
  const sessionShownRef = useRef(false);

  // Compute delta on stats arrival
  useEffect(() => {
    if (!slug || !stats) return;
    if (sessionShownRef.current) return;
    // Only ever show this banner once per session per slug
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(SESSION_KEY(slug)) === '1'; } catch { /* */ }
    if (alreadyShown) return;

    const seen = readSeen(slug);
    if (!seen) {
      // First visit — quietly stamp current state, no banner
      writeSeen(slug, stats.photo_count, stats.message_count);
      return;
    }
    const dp = Math.max((stats.photo_count || 0) - (seen.photos || 0), 0);
    const dm = Math.max((stats.message_count || 0) - (seen.messages || 0), 0);
    if (dp + dm <= 0) return;

    setDelta({ photos: dp, messages: dm });
    setVisible(true);
    sessionShownRef.current = true;
    try { sessionStorage.setItem(SESSION_KEY(slug), '1'); } catch { /* */ }
  }, [slug, stats]);

  // Fade on scroll past the hero (~600px)
  useEffect(() => {
    if (!visible) return;
    const onScroll = () => {
      if (window.scrollY > 600 && !fading) setFading(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [visible, fading]);

  // Auto-clear from DOM after the fade
  useEffect(() => {
    if (!fading) return;
    const t = setTimeout(() => setVisible(false), 600);
    return () => clearTimeout(t);
  }, [fading]);

  if (!visible) return null;

  const totalNew = delta.photos + delta.messages;
  // Variant copy: pre vs post purchase
  const headline = purchased
    ? 'New memories have been added since your last visit'
    : (totalNew === 1
        ? '1 new memory added since your last visit'
        : `${totalNew} new memories added since your last visit`);
  const ctaLabel = purchased ? 'View latest version' : 'View what\u2019s new';

  const onCta = () => {
    writeSeen(slug, (delta.photos + (readSeen(slug)?.photos || 0)),
              (delta.messages + (readSeen(slug)?.messages || 0)));
    setFading(true);
    // Smooth-scroll into the photo strip section
    const el = document.getElementById('year6-pv-photos');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onDismiss = () => {
    writeSeen(slug, (delta.photos + (readSeen(slug)?.photos || 0)),
              (delta.messages + (readSeen(slug)?.messages || 0)));
    setFading(true);
  };

  return (
    <div
      className="fixed top-3 left-3 right-3 z-[80] mx-auto max-w-2xl rounded-xl border border-amber-300/30 bg-amber-300/10 backdrop-blur-md px-4 py-3 flex items-center gap-3 transition-opacity duration-500"
      style={{ opacity: fading ? 0 : 1 }}
      data-testid="year6-pv-newcontent-banner"
    >
      <Sparkle size={16} weight="fill" className="text-amber-300 shrink-0" />
      <p className="flex-1 text-[13px] text-amber-100 font-bold leading-tight" data-testid="year6-pv-newcontent-banner-text">
        {headline}
      </p>
      <button
        onClick={onCta}
        className="shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-[12px] font-black text-black bg-amber-300 hover:bg-amber-400"
        data-testid="year6-pv-newcontent-banner-cta"
      >
        {ctaLabel}
      </button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded-full text-amber-200/70 hover:text-amber-100 hover:bg-amber-300/15"
        data-testid="year6-pv-newcontent-banner-dismiss"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// 2. PARENT SUBSCRIBE CARD — above the paywall
// ─────────────────────────────────────────────────────────────────
export function ParentSubscribeCard({ eventId }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Hide on remount if already subscribed in this session/browser
  useEffect(() => {
    try {
      if (eventId && localStorage.getItem(`year6_subscribed_${eventId}`) === '1') {
        setDone(true);
      }
    } catch { /* */ }
  }, [eventId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !eventId) return;
    setSubmitting(true);
    try {
      let anonId = null;
      try { anonId = localStorage.getItem('anon_id'); } catch { /* */ }
      await axios.post(`${API}/events/${eventId}/year6/parent-subscribe`, {
        email: email.trim(), anon_id: anonId,
      });
      try { localStorage.setItem(`year6_subscribed_${eventId}`, '1'); } catch { /* */ }
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not subscribe — check the email and try again';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-xl border border-emerald-400/25 bg-emerald-400/8 p-3.5 mb-4 inline-flex items-center gap-2"
        data-testid="year6-pv-subscribe-done"
      >
        <Check size={14} weight="bold" className="text-emerald-300 shrink-0" />
        <p className="text-emerald-200 text-[13px] font-bold leading-tight">
          {"You\u2019ll be notified when new memories are added"}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-4"
      data-testid="year6-pv-subscribe-card"
    >
      <p className="text-white text-[14px] font-black inline-flex items-center gap-1.5 mb-1">
        <EnvelopeSimple size={14} weight="fill" className="text-amber-300" />
        Get notified when new memories are added
      </p>
      <p className="text-white/55 text-[12px] leading-relaxed mb-3">
        {"We\u2019ll only send updates when something new is added."}
      </p>
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-white/[0.06] border border-white/15 text-white text-[13px] placeholder:text-white/35 focus:outline-none focus:border-amber-300/60"
          data-testid="year6-pv-subscribe-input"
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-[12.5px] font-black text-black bg-amber-300 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="year6-pv-subscribe-submit"
        >
          {submitting ? '\u2026' : 'Notify me'}
        </button>
      </div>
    </form>
  );
}


// ─────────────────────────────────────────────────────────────────
// 3. UPLOADS-TODAY FEEDBACK PILL — Control Panel
// ─────────────────────────────────────────────────────────────────
export function UploadsTodayPill({ count }) {
  if (!count || count <= 0) return null;
  return (
    <div
      className="rounded-lg bg-emerald-500/8 border border-emerald-500/20 px-3 py-2 mb-3 inline-flex items-center gap-2"
      data-testid="year6-control-uploads-today"
    >
      <TrendUp size={13} weight="fill" className="text-emerald-300" />
      <p className="text-emerald-200 text-[12.5px] font-bold">
        {count} new upload{count === 1 ? '' : 's'} today
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// 4. VERSION UPDATES PILL — Control Panel ("Memory Book v2" psychology)
// ─────────────────────────────────────────────────────────────────
export function VersionUpdatesPill({ count }) {
  if (!count || count <= 0) return null;
  return (
    <div
      className="rounded-lg bg-[#B89762]/30/8 border border-[#B89762]/40/20 px-3 py-2 mb-3 inline-flex items-center gap-2"
      data-testid="year6-control-version-updates"
    >
      <TrendUp size={13} weight="fill" className="text-[#C29D52]/85" />
      <p className="text-[#F4E9D2] text-[12.5px] font-bold">
        Your Memory Book has been updated {count} time{count === 1 ? '' : 's'} today
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// 5. "UPDATED SINCE LAST DOWNLOAD" BADGE — Parent View (purchased only)
// ─────────────────────────────────────────────────────────────────
export function UpdatedSinceLastDownloadBadge({ eventId, version }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!eventId || !version) return;
    try {
      const seenStr = localStorage.getItem(`year6_last_downloaded_version_${eventId}`);
      const seen = seenStr ? parseInt(seenStr, 10) : 0;
      if ((version || 0) > seen) setShow(true);
    } catch { /* */ }
  }, [eventId, version]);

  if (!show) return null;
  return (
    <div
      className="rounded-lg bg-[#B89762]/30/[0.08] border border-[#B89762]/40/25 px-3 py-2 mb-3 inline-flex items-center gap-2"
      data-testid="year6-pv-updated-badge"
    >
      <Sparkle size={12} weight="fill" className="text-[#C29D52]/85" />
      <p className="text-[#F4E9D2] text-[12px] font-bold">
        New version available — updated since your last download
      </p>
    </div>
  );
}

/* Helper used by the download path to stamp the seen version. */
export function stampDownloadedVersion(eventId, version) {
  if (!eventId || !version) return;
  try {
    localStorage.setItem(`year6_last_downloaded_version_${eventId}`, String(version));
  } catch { /* */ }
}
